import { useState, useEffect } from 'react';

interface Review {
  id: string;
  name: string;
  rating: number;
  review: string;
  date: string;
  status: 'pending' | 'approved';
}

export default function ManageReviews() {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [activeTab, setActiveTab] = useState<'pending' | 'approved'>('pending');
  const [copiedLink, setCopiedLink] = useState(false);

  // Get the review submission URL
  const reviewUrl = `${window.location.origin}/submit-review`;

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = () => {
    const stored = localStorage.getItem('customerReviews');
    if (stored) {
      setReviews(JSON.parse(stored));
    }
  };

  const approveReview = (id: string) => {
    const updated = reviews.map(review =>
      review.id === id ? { ...review, status: 'approved' as const } : review
    );
    setReviews(updated);
    localStorage.setItem('customerReviews', JSON.stringify(updated));
  };

  const rejectReview = (id: string) => {
    const updated = reviews.filter(review => review.id !== id);
    setReviews(updated);
    localStorage.setItem('customerReviews', JSON.stringify(updated));
  };

  const deleteReview = (id: string) => {
    const updated = reviews.filter(review => review.id !== id);
    setReviews(updated);
    localStorage.setItem('customerReviews', JSON.stringify(updated));
  };

  const copyLink = () => {
    navigator.clipboard.writeText(reviewUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const downloadQRCode = () => {
    // Generate QR code URL using a free QR code API
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(reviewUrl)}`;
    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = 'malfi-coffee-review-qr-code.png';
    link.click();
  };

  const filteredReviews = reviews.filter(review => review.status === activeTab);

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Header */}
      <div className="bg-white border-b-4 border-amber-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-4xl font-bold text-gray-900 text-center">Manage Customer Reviews</h1>
          <p className="text-gray-600 text-center mt-2">Review and approve customer submissions</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Share Review Link Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border-4 border-amber-300">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-share-line text-amber-600"></i>
            Share Review Form with Customers
          </h2>
          <p className="text-gray-600 mb-6">Share this link or QR code with your customers so they can submit reviews</p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Link Section */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Review Submission Link</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={reviewUrl}
                  readOnly
                  className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-50 text-gray-700 text-sm"
                />
                <button
                  onClick={copyLink}
                  className="px-6 py-3 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className={`${copiedLink ? 'ri-check-line' : 'ri-file-copy-line'}`}></i>
                  {copiedLink ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                <i className="ri-information-line"></i> Share this link via email, text message, or social media
              </p>
            </div>

            {/* QR Code Section */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">QR Code</label>
              <div className="flex items-center gap-4">
                <div className="bg-white p-3 rounded-lg border-2 border-gray-300">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(reviewUrl)}`}
                    alt="Review Form QR Code"
                    className="w-32 h-32"
                  />
                </div>
                <div className="flex-1">
                  <button
                    onClick={downloadQRCode}
                    className="w-full px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    <i className="ri-download-line"></i>
                    Download QR Code
                  </button>
                  <p className="text-sm text-gray-500 mt-2">
                    <i className="ri-information-line"></i> Print and display at your store
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'pending'
                ? 'bg-amber-500 text-white'
                : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-amber-400'
            }`}
          >
            Pending Reviews ({reviews.filter(r => r.status === 'pending').length})
          </button>
          <button
            onClick={() => setActiveTab('approved')}
            className={`px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'approved'
                ? 'bg-amber-500 text-white'
                : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-amber-400'
            }`}
          >
            Approved Reviews ({reviews.filter(r => r.status === 'approved').length})
          </button>
        </div>

        {/* Reviews List */}
        {filteredReviews.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center border-4 border-amber-300">
            <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-500 text-lg">
              {activeTab === 'pending' ? 'No pending reviews' : 'No approved reviews yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredReviews.map(review => (
              <div key={review.id} className="bg-white rounded-2xl shadow-lg p-6 border-4 border-amber-300">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-900">{review.name}</h3>
                      <div className="flex gap-1">
                        {[...Array(5)].map((_, i) => (
                          <i
                            key={i}
                            className={`${
                              i < review.rating ? 'ri-star-fill text-amber-500' : 'ri-star-line text-gray-300'
                            }`}
                          ></i>
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-700 mb-2">{review.review}</p>
                    <p className="text-sm text-gray-500">{review.date}</p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    {activeTab === 'pending' ? (
                      <>
                        <button
                          onClick={() => approveReview(review.id)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors whitespace-nowrap flex items-center gap-2"
                        >
                          <i className="ri-check-line"></i>
                          Approve
                        </button>
                        <button
                          onClick={() => rejectReview(review.id)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors whitespace-nowrap flex items-center gap-2"
                        >
                          <i className="ri-close-line"></i>
                          Reject
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => deleteReview(review.id)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors whitespace-nowrap flex items-center gap-2"
                      >
                        <i className="ri-delete-bin-line"></i>
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}